/**
 * File asset store: fetch → decode → cache, keyed by AudioAsset id.
 *
 * - Bytes are fetched as soon as `register` is called (menu/boot time, never in a frame) — unless the assets are
 *   registered `defer`red: then only their metadata is known (captions, durations) until `prefetch` (or a first
 *   use) fetches them, so the app can load in tiers instead of pulling every file over the network at boot.
 * - Non-music assets are decoded right away: with the live AudioContext when it exists, otherwise with an
 *   OfflineAudioContext (decoding needs no user gesture), so announcer lines are ready the moment audio unlocks.
 * - Music is large (≈11 MB of PCM per 30 s stereo track), so it is decoded on demand and only the two most
 *   recently used tracks stay decoded (the compressed bytes are kept for a fast re-decode).
 * - Loading runs through one small queue (fetches / native decoder threads). An on-demand `decode` (the track a screen
 *   just asked for, an announcer line that missed) and an `urgent` prefetch take a priority lane: ahead of the bulk
 *   tier prefetch, and allowed one slot over the limit, so they never wait behind 100+ announcer files.
 * - A failed asset is reported once (console.warn) and simply never plays; the synth fallbacks cover SFX. An
 *   in-place decode (native) is retried once after `retryDelayMs` first: a dev-server hiccup, an expo-asset copy
 *   still being written or a moment of memory pressure must not poison the file for the session.
 */
import type { AudioAsset } from './index';

export type Decoder = Pick<BaseAudioContext, 'decodeAudioData'>;
export type Fetcher = (url: string) => Promise<ArrayBuffer>;
/**
 * Decodes an asset straight from where it lives, without its bytes ever entering JS (react-native-audio-api reads a
 * local file natively: `decodeAudioData(fileUri)`). `url` is the asset url joined with the base url. Resolves to the
 * buffer, or rejects (the asset then fails like a broken fetch). Replaces fetch + `decodeAudioData` for every asset.
 */
export type FileDecoder = (asset: AudioAsset, url: string) => Promise<AudioBuffer>;

export interface AssetStoreOptions {
  /** Native: decode files in place (no fetch, no compressed bytes kept in JS). */
  decodeFile?: FileDecoder | null;
  /** How many decoded music tracks stay in memory (default `MUSIC_CACHE`; native low-memory devices: 1). */
  musicCache?: number;
  /** In-place decode: delay before the one retry of a failed file (ms, default 1500). */
  retryDelayMs?: number;
  /** Timer for the retry (tests inject a manual clock). Default `setTimeout`. */
  setTimeout?: (fn: () => void, ms: number) => unknown;
}

interface Entry {
  asset: AudioAsset;
  bytes: ArrayBuffer | null;
  fetching: Promise<ArrayBuffer | null> | null;
  buffer: AudioBuffer | null;
  decoding: Promise<AudioBuffer | null> | null;
  failed: boolean;
  lastUsed: number;
  /** Registered with `defer`: nothing fetched until `prefetch` / first use. */
  deferred: boolean;
  /** In-place decode attempts that failed (one retry is allowed). */
  failures: number;
  /** Its load is queued for a slot (an on-demand ask promotes it to the priority lane). */
  waiter: Waiter | null;
}

interface Waiter {
  start: () => void;
  limit: number;
  /** Priority lane: ahead of bulk waiters, and allowed one slot over the limit. */
  urgent: boolean;
}

/** Load priority: a direct ask (`urgent`) or the bulk tier prefetch / registration. */
interface LoadPriority {
  urgent: boolean;
}
const URGENT: LoadPriority = { urgent: true };
const BULK: LoadPriority = { urgent: false };

/** How many decoded music tracks stay in memory. */
export const MUSIC_CACHE = 2;
const FETCH_CONCURRENCY = 4;
/** In-place file decodes (native decoder threads) running at once. */
const FILE_DECODE_CONCURRENCY = 3;
/** One retry of a failed in-place decode, after this long. */
const DECODE_RETRY_MS = 1500;

export class AssetStore {
  private readonly entries = new Map<string, Entry>();
  private readonly waiters: Waiter[] = [];
  private inFlight = 0;
  private useCounter = 0;
  private disposed = false;
  private readonly decodeFile: FileDecoder | null;
  private readonly musicCache: number;
  private readonly retryDelayMs: number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;

  constructor(
    private readonly fetcher: Fetcher,
    /** Returns the best decoder available right now (live context or an offline one), or null. */
    private readonly decoder: () => Decoder | null,
    private readonly baseUrl = '',
    private readonly warn: (msg: string) => void = () => {},
    opts: AssetStoreOptions = {},
  ) {
    this.decodeFile = opts.decodeFile ?? null;
    this.musicCache = Math.max(1, Math.floor(opts.musicCache ?? MUSIC_CACHE));
    this.retryDelayMs = Math.max(0, opts.retryDelayMs ?? DECODE_RETRY_MS);
    this.setTimer = opts.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  }

  /** Bytes of decoded PCM held right now (≈ native memory: samples × channels × 4). */
  get decodedBytes(): number {
    let n = 0;
    for (const e of this.entries.values())
      if (e.buffer) n += e.buffer.length * e.buffer.numberOfChannels * 4;
    return n;
  }

  get registered(): number {
    return this.entries.size;
  }

  get loaded(): number {
    let n = 0;
    for (const e of this.entries.values()) if (e.buffer) n++;
    return n;
  }

  has(id: string): boolean {
    return this.entries.has(id);
  }

  asset(id: string): AudioAsset | undefined {
    return this.entries.get(id)?.asset;
  }

  /** Decoded buffer if ready (marks it used). */
  buffer(id: string): AudioBuffer | null {
    const e = this.entries.get(id);
    if (!e?.buffer) return null;
    e.lastUsed = ++this.useCounter;
    return e.buffer;
  }

  /**
   * Register assets and start loading them. Resolves when every non-music asset is decoded (or failed). With
   * `defer`, only the metadata is registered (resolves at once); `prefetch` loads them later.
   */
  register(assets: readonly AudioAsset[], o: { defer?: boolean } = {}): Promise<void> {
    const pending: Promise<unknown>[] = [];
    for (const asset of assets) {
      if (!asset || typeof asset.id !== 'string' || typeof asset.url !== 'string') continue;
      let e = this.entries.get(asset.id);
      if (e && e.asset.url === asset.url) {
        e.asset = asset; // metadata refresh (captions, gains)
        if (!o.defer) e.deferred = false;
      } else {
        e = {
          asset,
          bytes: null,
          fetching: null,
          buffer: null,
          decoding: null,
          failed: false,
          lastUsed: 0,
          deferred: !!o.defer,
          failures: 0,
          waiter: null,
        };
        this.entries.set(asset.id, e);
      }
      if (e.deferred) continue;
      pending.push(asset.bus === 'music' ? this.fetch(e, BULK) : this.load(asset.id, BULK));
    }
    return Promise.all(pending).then(() => undefined);
  }

  /**
   * Load deferred assets (all of them, or those `filter` picks) in registration order: non-music files are
   * decoded, music only fetched (it decodes on demand). Resolves when they are ready (or failed). `urgent`: in the
   * priority lane (see the header), also promoting any of them already queued.
   */
  prefetch(filter?: (asset: AudioAsset) => boolean, o: { urgent?: boolean } = {}): Promise<void> {
    const pri = o.urgent ? URGENT : BULK;
    const pending: Promise<unknown>[] = [];
    for (const [id, e] of this.entries) {
      if (filter && !filter(e.asset)) continue;
      if (!e.deferred) {
        // already on its way: an urgent prefetch still jumps it ahead of the bulk queue
        if (pri.urgent && e.asset.bus !== 'music' && (e.decoding || e.fetching)) {
          this.promote(e);
          pending.push((e.decoding ?? e.fetching)!);
        }
        continue;
      }
      e.deferred = false;
      pending.push(e.asset.bus === 'music' ? this.fetch(e, pri) : this.load(id, pri));
    }
    return Promise.all(pending).then(() => undefined);
  }

  /**
   * Decode (fetching first if needed) — an on-demand ask: it takes the priority lane (ahead of a bulk prefetch,
   * promoting the file if it is already queued). Concurrent calls share one promise.
   */
  decode(id: string): Promise<AudioBuffer | null> {
    return this.load(id, URGENT);
  }

  private load(id: string, pri: LoadPriority): Promise<AudioBuffer | null> {
    const e = this.entries.get(id);
    if (!e) return Promise.resolve(null);
    e.deferred = false; // asked for: it is wanted now
    if (e.buffer) return Promise.resolve(e.buffer);
    if (e.decoding) {
      if (pri.urgent) this.promote(e);
      return e.decoding;
    }
    if (e.failed) return Promise.resolve(null);
    if (this.decodeFile) return (e.decoding = this.decodeInPlace(id, e, this.decodeFile, pri));
    const p = this.fetch(e, pri).then(async (bytes) => {
      if (!bytes || this.disposed) return null;
      const dec = this.decoder();
      if (!dec) return null;
      try {
        // decodeAudioData detaches its argument: keep our copy for music re-decodes.
        const buf = await decodeCompat(dec, bytes.slice(0));
        if (this.disposed) return null;
        e.buffer = buf;
        e.lastUsed = ++this.useCounter;
        if (e.asset.bus !== 'music')
          e.bytes = null; // keep compressed bytes only for evictable music
        else this.evictMusic(id);
        return buf;
      } catch (err) {
        e.failed = true;
        this.warn(`[audio] cannot decode ${e.asset.url}: ${String(err)}`);
        return null;
      } finally {
        e.decoding = null;
      }
    });
    e.decoding = p;
    return p;
  }

  /** Re-decode everything that is not decoded yet (e.g. once a live context exists). */
  decodeAllPending(): Promise<void> {
    const ps: Promise<unknown>[] = [];
    for (const [id, e] of this.entries) {
      if (e.asset.bus === 'music' || e.buffer || e.failed || e.deferred) continue;
      ps.push(this.load(id, BULK));
    }
    return Promise.all(ps).then(() => undefined);
  }

  /**
   * `decodeFile` path: the decoder reads the file itself (nothing fetched, no compressed bytes kept). A failure is
   * retried once after `retryDelayMs` (the same promise resolves with the retry's result); the second one is final.
   */
  private decodeInPlace(
    id: string,
    e: Entry,
    decodeFile: FileDecoder,
    pri: LoadPriority,
  ): Promise<AudioBuffer | null> {
    const url = resolveUrl(this.baseUrl, e.asset.url);
    const attempt = (p: LoadPriority): Promise<AudioBuffer | null> => {
      let held = true;
      const free = (): void => {
        if (!held) return;
        held = false;
        this.release();
      };
      return this.slot(FILE_DECODE_CONCURRENCY, p, e)
        .then(() => (this.disposed ? null : decodeFile(e.asset, url)))
        .then(
          (buf) => {
            free();
            if (!buf || this.disposed) return null;
            e.buffer = buf;
            e.failures = 0;
            e.lastUsed = ++this.useCounter;
            if (e.asset.bus === 'music') this.evictMusic(id);
            return buf;
          },
          (err: unknown) => {
            free();
            if (this.disposed) return null;
            if (++e.failures <= 1) {
              this.warn(`[audio] decode failed, retrying in ${this.retryDelayMs} ms: ${url}: ${String(err)}`);
              return new Promise<void>((r) => void this.setTimer(r, this.retryDelayMs)).then(() =>
                this.disposed ? null : attempt(p),
              );
            }
            e.failed = true;
            this.warn(`[audio] cannot decode ${url}: ${String(err)}`);
            return null;
          },
        );
    };
    return attempt(pri).finally(() => {
      e.decoding = null;
    });
  }

  dispose(): void {
    this.disposed = true;
    this.entries.clear();
    this.waiters.length = 0;
  }

  private evictMusic(keep: string): void {
    const music = [...this.entries.entries()].filter(([, e]) => e.asset.bus === 'music' && e.buffer);
    if (music.length <= this.musicCache) return;
    music.sort((a, b) => b[1].lastUsed - a[1].lastUsed);
    for (const [id, e] of music.slice(this.musicCache)) if (id !== keep) e.buffer = null;
  }

  private fetch(e: Entry, pri: LoadPriority = URGENT): Promise<ArrayBuffer | null> {
    // decoding in place: there is nothing to fetch ahead (music "prefetch" = known to exist)
    if (this.decodeFile) return Promise.resolve(null);
    if (e.bytes) return Promise.resolve(e.bytes);
    if (e.fetching) {
      if (pri.urgent) this.promote(e);
      return e.fetching;
    }
    if (e.failed) return Promise.resolve(null);
    const url = resolveUrl(this.baseUrl, e.asset.url);
    const p = this.slot(FETCH_CONCURRENCY, pri, e)
      .then(() => this.fetcher(url))
      .then((bytes) => {
        e.bytes = bytes;
        return bytes;
      })
      .catch((err: unknown) => {
        e.failed = true;
        this.warn(`[audio] cannot load ${url}: ${String(err)}`);
        return null;
      })
      .finally(() => {
        e.fetching = null;
        this.release();
      });
    e.fetching = p;
    return p;
  }

  private release(): void {
    this.inFlight--;
    this.pump();
  }

  /** Start queued loads while their lane has room (urgent waiters are always at the front). */
  private pump(): void {
    while (this.waiters.length > 0) {
      const w = this.waiters[0]!;
      if (this.inFlight >= w.limit + (w.urgent ? 1 : 0)) return;
      this.waiters.shift();
      w.start();
    }
  }

  private enqueue(w: Waiter): void {
    if (!w.urgent) {
      this.waiters.push(w);
      return;
    }
    const i = this.waiters.findIndex((x) => !x.urgent);
    this.waiters.splice(i < 0 ? this.waiters.length : i, 0, w);
  }

  /** A queued load was asked for directly: move it to the priority lane (starting it if that lane has room). */
  private promote(e: Entry): void {
    const w = e.waiter;
    if (!w || w.urgent) return;
    const i = this.waiters.indexOf(w);
    if (i < 0) return;
    this.waiters.splice(i, 1);
    w.urgent = true;
    this.enqueue(w);
    this.pump();
  }

  /**
   * Concurrency limiter for fetches (and in-place decodes): `limit` at once for the bulk lane, one more for the
   * priority lane, which is served first.
   */
  private slot(limit: number, pri: LoadPriority, e?: Entry): Promise<void> {
    if (this.inFlight < limit + (pri.urgent ? 1 : 0) && (pri.urgent || this.waiters.length === 0)) {
      this.inFlight++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const w: Waiter = {
        limit,
        urgent: pri.urgent,
        start: () => {
          this.inFlight++;
          if (e && e.waiter === w) e.waiter = null;
          resolve();
        },
      };
      if (e) e.waiter = w;
      this.enqueue(w);
    });
  }
}

/** Join a base URL (e.g. Vite's BASE_URL "/game/") with an asset URL ("/audio/x.mp3"). Absolute URLs pass through. */
export const resolveUrl = (base: string, url: string): string => {
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || !base || base === '/') return url;
  return base.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '');
};

/** Promise-based decode that also supports old Safari's callback-only signature. */
export const decodeCompat = (dec: Decoder, bytes: ArrayBuffer): Promise<AudioBuffer> =>
  new Promise<AudioBuffer>((resolve, reject) => {
    const r = dec.decodeAudioData(bytes, resolve, reject) as Promise<AudioBuffer> | undefined;
    if (r && typeof r.then === 'function') r.then(resolve, reject);
  });
